import { redirect } from 'next/navigation';
import { FC } from 'react';
import { ProductGridStoryblok } from 'storyblok-types';
import { ProductListing } from 'pages/ProductListing';
import { CategoriesBubbles } from 'pages/ProductListing/CategoriesBubbles';
import { IStoryBlokComponentProps } from 'pages/StoryBlok/types';
import { CustomCategoriesNames } from 'modules/shop/navigationManager/types';
import { getPageBanners } from 'commerce/services/banner';
import {
  getCategoryProductsBackend,
  getLongTailProductsBackend,
} from 'commerce/shop/backend';
import { getBannersUpsellingStories } from 'commerce/storyblok/api';
import { ClientPageType } from 'src/app/types';
import { parseLongTailQuery, parseSearchQuery } from './ParseQuery';

const getCategoryName = (
  story: IStoryBlokComponentProps<ProductGridStoryblok>['story'],
  isLongTailPage: boolean,
) => {
  if (story.path === '/' || isLongTailPage) {
    return undefined;
  }

  if (story.lang === 'default') {
    return story.name;
  }

  const translatedStory = story.translated_slugs?.find(
    (translated) => translated.lang === story.lang,
  );

  return translatedStory?.name || story.name;
};

export const View1: FC<
  IStoryBlokComponentProps<ProductGridStoryblok>
> = async ({
  story,
  blok,
  serverProps: { page = 1, params, queryParams, pageType },
}) => {
  const slug = params.category
    ? `${params.category?.join('/')}/`
    : ClientPageType.HOME;

  const isLongTailPage = !!blok.searchQuery;

  // if home page OR long tail page, categoryName = undefined
  const categoryName = getCategoryName(story, isLongTailPage);

  const searchQuery = isLongTailPage
    ? parseLongTailQuery(blok.searchQuery)
    : parseSearchQuery(queryParams, blok.searchQuery);

  const {
    facets,
    filters,
    sortItems,
    hits,
    totalHits,
    pageCount,
    sectionBanners,
  } = await Promise.all([
    // make a request with real search query parameters to get filtered products and actual paging
    isLongTailPage
      ? getLongTailProductsBackend({
          categoryName,
          page,
          searchQuery,
        })
      : getCategoryProductsBackend({
          categoryName,
          page,
          searchQuery,
        }),
    // make an additional request to get facets, since using searchQuery will return incorrect facets
    getCategoryProductsBackend({
      categoryName,
      page,
    }),
    getPageBanners({ idx: page, slug }),
  ]).then(
    ([
      { data: realResponse },
      { data: facetsResponse, error },
      bannersResp,
    ]) => {
      const sectionBanners = bannersResp.response || [];
      if (!facetsResponse || !!error || !realResponse) {
        console.error(error);
        return {};
      }
      const { facets } = facetsResponse;
      const { hits, sortItems, totalHits, filters, paging } = realResponse;

      return {
        facets,
        filters,
        sortItems,
        hits,
        totalHits,
        pageCount: paging?.pageCount,
        sectionBanners,
      };
    },
  );

  /** request from qa to set section to 1 if current section doesn't exist */
  const notSection = page && pageCount && pageCount < Number(page);
  if (notSection) {
    /** redirect to same page but without any query params */
    return redirect(
      pageType === ClientPageType.CATEGORY
        ? `/${params?.category?.join('/')}`
        : '/',
    );
  }

  const hasCategoryChips = !!story.content.body?.find((item) => item?.chips);

  const { response: bannersResponse } = await getBannersUpsellingStories(
    params.locale,
  );
  const upsellingBanners = bannersResponse?.data?.story?.content.banners;

  return (
    <>
      <CategoriesBubbles
        categories={blok.categoriesBubbles}
        categorySlug={params.category?.join('/')}
        hasChips={hasCategoryChips}
      />

      <ProductListing
        title={blok.title}
        facets={facets || []}
        initProducts={hits}
        sortItems={sortItems}
        totalProducts={totalHits}
        initSection={Number(page)}
        lastPage={pageCount || 1}
        categoryName={categoryName}
        initBanners={sectionBanners}
        gtmPageName={
          pageType === ClientPageType.HOME
            ? CustomCategoriesNames.homeCategory
            : categoryName
        } /** need to set correct product category for gtm  */
        configuration={{
          isPrimaryPage: story.content.isPrimaryPage,
          showFilters: blok.showFilters,
          displaySalePrices: story.content.displaySalePrices,
        }}
        initFilters={filters || []}
        initQuery={searchQuery?.get('query') || ''}
        upsellingBanners={upsellingBanners}
      />
    </>
  );
};
